import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useLoading } from './LoadingContext';

const AuthContext = createContext();

// Normalize dashboard user roles so any admin-like role gets full admin capabilities
const normalizeDashboardUser = (user) => {
  if (!user) return user;
  const originalRole = user.role || '';
  const normalizedRole = String(originalRole).toLowerCase();
  const roleName = String(user.role_name || user.role_display_name || '').toLowerCase();
  const roleLevel = Number(user.role_level || 0);
  const isAdminLike = normalizedRole.includes('admin') || roleName.includes('super admin') || roleLevel >= 10;
  return {
    ...user,
    originalRole,
    role: isAdminLike ? 'admin' : originalRole
  };
};

// Storage keys for separate authentication
const ECOMMERCE_TOKEN_KEY = 'ecommerce_token';
const ECOMMERCE_USER_KEY = 'ecommerce_user';
const DASHBOARD_TOKEN_KEY = 'dashboard_token';
const DASHBOARD_USER_KEY = 'dashboard_user';
const BOOTSTRAP_FETCH_TIMEOUT_MS = 8000;

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [ecommerceUser, setEcommerceUser] = useState(null);
  const [dashboardUser, setDashboardUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const { track } = useLoading();
  const didInitRef = useRef(false);

  const API_BASE = process.env.REACT_APP_API_URL || '';

  const fetchWithTimeout = useCallback(async (url, options = {}, timeoutMs = BOOTSTRAP_FETCH_TIMEOUT_MS) => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
    try {
      return await fetch(url, {
        ...options,
        signal: controller.signal
      });
    } finally {
      clearTimeout(timeoutId);
    }
  }, []);

  // Determine if we're on a dashboard route
  const isDashboardRoute = location.pathname.startsWith('/dashboard');

  // Get the appropriate user based on current route
  const user = isDashboardRoute ? dashboardUser : ecommerceUser;
  const isAuthenticated = isDashboardRoute ? !!dashboardUser : !!ecommerceUser;

  // Get the appropriate token based on current route
  const getToken = useCallback((forDashboard = null) => {
    const useDashboard = forDashboard !== null ? forDashboard : isDashboardRoute;
    return useDashboard 
      ? localStorage.getItem(DASHBOARD_TOKEN_KEY)
      : localStorage.getItem(ECOMMERCE_TOKEN_KEY);
  }, [isDashboardRoute]);

  const checkAuthStatus = useCallback(async () => {
    setLoading(true);
    try {
      await track(async () => {
        // Check e-commerce auth
        const ecommerceToken = localStorage.getItem(ECOMMERCE_TOKEN_KEY);
        if (ecommerceToken) {
          try {
            const response = await fetchWithTimeout(`${API_BASE}/api/auth/profile`, {
              headers: { 'Authorization': `Bearer ${ecommerceToken}` }
            });
            if (response.ok) {
              const data = await response.json();
              setEcommerceUser(data.data);
              localStorage.setItem(ECOMMERCE_USER_KEY, JSON.stringify(data.data));
            } else {
              localStorage.removeItem(ECOMMERCE_TOKEN_KEY);
              localStorage.removeItem(ECOMMERCE_USER_KEY);
            }
          } catch (error) {
            console.error('E-commerce auth check failed:', error);
            localStorage.removeItem(ECOMMERCE_TOKEN_KEY);
            localStorage.removeItem(ECOMMERCE_USER_KEY);
          }
        } else {
          // Try to restore from stored user data
          const storedUser = localStorage.getItem(ECOMMERCE_USER_KEY);
          if (storedUser) {
            try {
              setEcommerceUser(JSON.parse(storedUser));
            } catch (e) {
              localStorage.removeItem(ECOMMERCE_USER_KEY);
            }
          }
        }

        // Check dashboard auth
        const dashboardToken = localStorage.getItem(DASHBOARD_TOKEN_KEY);
        if (dashboardToken) {
          try {
            const response = await fetchWithTimeout(`${API_BASE}/api/auth/profile`, {
              headers: { 'Authorization': `Bearer ${dashboardToken}` }
            });
            if (response.ok) {
              const data = await response.json();
              const normalizedUser = normalizeDashboardUser(data.data);
              setDashboardUser(normalizedUser);
              localStorage.setItem(DASHBOARD_USER_KEY, JSON.stringify(normalizedUser));
            } else {
              localStorage.removeItem(DASHBOARD_TOKEN_KEY);
              localStorage.removeItem(DASHBOARD_USER_KEY);
            }
          } catch (error) {
            console.error('Dashboard auth check failed:', error);
            localStorage.removeItem(DASHBOARD_TOKEN_KEY);
            localStorage.removeItem(DASHBOARD_USER_KEY);
          }
        } else {
          // Try to restore from stored user data
          const storedUser = localStorage.getItem(DASHBOARD_USER_KEY);
          if (storedUser) {
            try {
              setDashboardUser(normalizeDashboardUser(JSON.parse(storedUser)));
            } catch (e) {
              localStorage.removeItem(DASHBOARD_USER_KEY);
            }
          }
        }
      });
    } catch (error) {
      console.error('Auth check failed:', error);
    } finally {
      setLoading(false);
    }
  }, [API_BASE, fetchWithTimeout, track]);

  useEffect(() => {
    // React 18 StrictMode runs effects twice in dev; avoid duplicate boot fetch.
    if (didInitRef.current) return;
    didInitRef.current = true;
    checkAuthStatus();
  }, [checkAuthStatus]);

  const login = async (emailOrToken, passwordOrUser, isDashboardLogin = false) => {
    try {
      // If second parameter is an object with id/email/role (user object), it means we already have token and user
      if (typeof passwordOrUser === 'object' && passwordOrUser !== null && passwordOrUser.id) {
        // Direct login with token and user (from dashboard login)
        const tokenKey = isDashboardLogin ? DASHBOARD_TOKEN_KEY : ECOMMERCE_TOKEN_KEY;
        const userKey = isDashboardLogin ? DASHBOARD_USER_KEY : ECOMMERCE_USER_KEY;
        const normalizedUser = isDashboardLogin ? normalizeDashboardUser(passwordOrUser) : passwordOrUser;
        localStorage.setItem(tokenKey, emailOrToken); // emailOrToken is actually token here
        localStorage.setItem(userKey, JSON.stringify(normalizedUser));
        if (isDashboardLogin) {
          setDashboardUser(normalizedUser);
        } else {
          setEcommerceUser(normalizedUser);
        }
        return { success: true };
      }

      // Regular login with email/phone and password
      const response = await fetch(`${API_BASE}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ loginId: emailOrToken, password: passwordOrUser })
      });

      if (!response.ok) {
        const contentType = response.headers.get('content-type') || '';
        let message = `Login failed (${response.status})`;

        if (contentType.includes('application/json')) {
          const errorData = await response.json().catch(() => null);
          message = errorData?.message || message;
        } else {
          const text = await response.text().catch(() => '');
          if (text && text.trim()) {
            message = text.replace(/\s+/g, ' ').trim().slice(0, 160);
          }
        }

        return { success: false, message };
      }

      const data = await response.json();
      
      if (data.success) {
        const tokenKey = isDashboardLogin ? DASHBOARD_TOKEN_KEY : ECOMMERCE_TOKEN_KEY;
        const userKey = isDashboardLogin ? DASHBOARD_USER_KEY : ECOMMERCE_USER_KEY;
        const normalizedUser = isDashboardLogin ? normalizeDashboardUser(data.data.user) : data.data.user;
        localStorage.setItem(tokenKey, data.data.token);
        localStorage.setItem(userKey, JSON.stringify(normalizedUser));
        if (isDashboardLogin) {
          setDashboardUser(normalizedUser);
        } else {
          setEcommerceUser(normalizedUser);
        }
        return { success: true };
      } else {
        return { success: false, message: data.message || 'Login failed' };
      }
    } catch (error) {
      console.error('Login failed:', error);
      return { success: false, message: error.message || 'Login failed. Please try again.' };
    }
  };

  const register = async (userData) => {
    try {
      const response = await fetch(`${API_BASE}/api/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(userData)
      });

      const data = await response.json();
      
      if (data.success) {
        // Registration is always for e-commerce users
        localStorage.setItem(ECOMMERCE_TOKEN_KEY, data.data.token);
        localStorage.setItem(ECOMMERCE_USER_KEY, JSON.stringify(data.data.user));
        setEcommerceUser(data.data.user);
        return { success: true };
      } else {
        return { success: false, message: data.message };
      }
    } catch (error) {
      console.error('Registration failed:', error);
      return { success: false, message: 'Registration failed. Please try again.' };
    }
  };

  const logout = (isDashboardLogout = false) => {
    if (isDashboardLogout) {
      localStorage.removeItem(DASHBOARD_TOKEN_KEY);
      localStorage.removeItem(DASHBOARD_USER_KEY);
      setDashboardUser(null);
    } else {
      localStorage.removeItem(ECOMMERCE_TOKEN_KEY);
      localStorage.removeItem(ECOMMERCE_USER_KEY);
      setEcommerceUser(null);
    }
  };

  const updateProfile = async (userData, isDashboardUpdate = false) => {
    try {
      const token = getToken(isDashboardUpdate);
      if (!token) {
        return { success: false, message: 'Not authenticated' };
      }

      const response = await fetch(`${API_BASE}/api/auth/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(userData)
      });

      const data = await response.json();
      
      if (data.success) {
        const userKey = isDashboardUpdate ? DASHBOARD_USER_KEY : ECOMMERCE_USER_KEY;
        localStorage.setItem(userKey, JSON.stringify(data.data));
        if (isDashboardUpdate) {
          setDashboardUser(data.data);
        } else {
          setEcommerceUser(data.data);
        }
        return { success: true };
      } else {
        return { success: false, message: data.message };
      }
    } catch (error) {
      console.error('Profile update failed:', error);
      return { success: false, message: 'Profile update failed. Please try again.' };
    }
  };

  const value = {
    user,
    ecommerceUser,
    dashboardUser,
    loading,
    login,
    register,
    logout,
    updateProfile,
    isAuthenticated,
    isAdmin: dashboardUser?.role === 'admin',
    getToken, // Expose getToken for components that need it
    isDashboardRoute
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
